import { Injectable } from '@angular/core';
import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Observable, of, Subject, throwError, timer } from 'rxjs';
import { AuthenticationService } from '../business/user/authentication.service';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

// ♻️ Gestion du refresh token si 401
@Injectable()
export class RefreshInterceptor implements HttpInterceptor {
    private waiters: Subject<string | null>[] = [];
    private refreshing = false;




    constructor(private auth: AuthenticationService, private http: HttpClient, private router: Router) {

    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isAuthRoute = req.url.includes('/Authentication/Refresh') || req.url.includes('/Authentication/Logout');

        const isCgvValidationRoute = this.router.url.includes('/terms-of-service/validate/');
        if (isAuthRoute || isCgvValidationRoute) { 
            return next.handle(req);
        }

        // jamais de refresh ni de retry sur ces routes
        if (isAuthRoute) {
            //console.log('[RefreshInterceptor] Skip (auth route):', req.url);
            return next.handle(req);
        }

        return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
                //console.log('[RefreshInterceptor] catchError', err.status, req.url);
                if (err.status !== 401) return throwError(err);

                // 🔒 Si l'onglet est inactif, on ignore ce 401
                if (document.hidden) {
                    console.warn('[RefreshInterceptor] 401 ignoré (onglet inactif).');
                    return of(err as any); // on renvoie un observable "vide" pour ne rien casser
                }

                //console.log('[RefreshInterceptor] 401 detected → trigger refreshOnce()');
                // 401 détecté → on tente un refresh
                const usedAuth = req.headers.get('Authorization') || '';
                const usedToken = usedAuth.startsWith('Bearer ') ? usedAuth.slice(7) : null;
                const currentToken = this.auth.getToken();

                // console.log('used Token: ' + usedToken);
                // console.log('current Token: ' + currentToken);
                if (usedToken && currentToken && usedToken !== currentToken) {
                    // Retry immédiat avec le token courant (pas d'attente, pas de refresh)
                    // console.log('[RefreshInterceptor] Token has changed meanwhile → retry request with current token');
                    const retried = req.clone({
                        setHeaders: { Authorization: `Bearer ${currentToken}` }
                    });
                    // console.log('Return retried');
                    // console.log(retried);
                    return next.handle(retried);
                }

                // Rafraîchissement déjà en cours → attendre la fin
                if (this.refreshing) {
                    // console.log('[RefreshInterceptor] Refresh déjà en cours → ajout à la pile');
                    return this.addToWaitingPile().pipe(
                        switchMap(newToken => {
                            if (!newToken) return throwError(() => err);
                            const retried = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
                            return next.handle(retried);
                        })
                    );
                }


                // 🚀 Démarrer le refresh
                return this.refreshOnce().pipe(
                    switchMap(newToken => {
                        if (!newToken) {
                            // console.error('[RefreshInterceptor] Refresh échoué → logout');
                            this.auth.logout(true);
                            return throwError(() => err);
                        }
                        const retried = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
                        return next.handle(retried);
                    })
                );
            })
        );
    }


    private refreshOnce(): Observable<string | null> {
        this.refreshing = true;
        // console.log('[RefreshInterceptor] Lancement refresh token...');

        return this.http.post<{ jwt: string }>(
            `${environment.baseApiUrl}/Authentication/Refresh`,
            {},
            { withCredentials: true }
        ).pipe(
            map(r => r.jwt),
            switchMap(token => {
                this.auth.setToken(token);
                // console.log('[RefreshInterceptor] Refresh réussi');
                this.resolveAllWaiters(token);
                return of(token);
            }),
            catchError((e: HttpErrorResponse) => {
                // console.error('[RefreshInterceptor] Refresh échoué → logout');
                return of(null);
            }),
            finalize(() => {
                this.refreshing = false;
            })
        );
    }

    private addToWaitingPile(): Observable<string | null> {
        const waiter = new Subject<string | null>();
        this.waiters.push(waiter);
        return waiter.asObservable();
    }

    private resolveAllWaiters(token: string | null) {
        // console.log(`[RefreshInterceptor] Résolution des ${this.waiters.length} requêtes en attente`);
        for (const waiter of this.waiters) {
            waiter.next(token);
            waiter.complete();
        }
        this.waiters = [];
    }
}